/** @format */
import { HttpErrorResponse } from '@angular/common/http';
import { DOCUMENT, Inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';
import { StateService } from '@uirouter/core';
import { Subject, takeUntil } from 'rxjs';
import { NavigationService } from '../navigation/navigation.service';
import { AuthService } from './auth-service';

/**
 * Per-app state name for the no-sisu-account landing page (e.g. 'student.no-sisu-account').
 * Each app provides this in its AppModule so the shared NoSisuAccountRedirectService
 * knows where to send the user.
 */
export const NO_SISU_ACCOUNT_STATE_NAME = new InjectionToken<string>('NO_SISU_ACCOUNT_STATE_NAME');

// 403s from /ori/preauth where the IdP session is alive but the user can't proceed in the app —
// route them to a landing page with a logout button instead of looping back to login.
const HANDLED_ACCESS_DENIED_TYPES: ReadonlySet<string> = new Set([
    'USER_NOT_FOUND',
    'MISSING_ROLE',
    'NO_LOGIN_TIME_WINDOW',
    'USER_LOGIN_DISABLED',
]);

const NO_SISU_ACCOUNT_PATH = '/no-sisu-account';

@Injectable({ providedIn: 'root' })
export class NoSisuAccountRedirectService implements OnDestroy {
    private readonly destroy$ = new Subject<void>();

    constructor(
        @Inject(NO_SISU_ACCOUNT_STATE_NAME) private stateName: string,
        private authService: AuthService,
        private stateService: StateService,
        private navigationService: NavigationService,
        @Inject(DOCUMENT) private document: Document,
    ) {
        this.authService.errorResponse$.pipe(takeUntil(this.destroy$)).subscribe(error => this.handle(error));
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // Match on the parsed pathname rather than `endsWith('/ori/preauth')` so a future query string
    // (`?foo=bar`) on the request URL doesn't silently disable this filter.
    private isPreauthUrl(url: string): boolean {
        try {
            return new URL(url, this.document.location.origin).pathname === '/ori/preauth';
        } catch {
            return false;
        }
    }

    private handle(error: HttpErrorResponse): void {
        if (error?.status !== 403) {
            return;
        }
        if (!error.url || !this.isPreauthUrl(error.url)) {
            return;
        }
        const accessDeniedType: string = error.error?.accessDeniedType;
        if (!HANDLED_ACCESS_DENIED_TYPES.has(accessDeniedType)) {
            return;
        }
        // Already on the landing page (e.g. the new bootstrap after a hard reload re-fired preauth).
        // A second navigation to the same URL would just reload the page in a loop.
        if (this.document.location.pathname.endsWith(NO_SISU_ACCOUNT_PATH)) {
            return;
        }
        // Hard browser navigation, not state.go: the previous user's data is still hydrating
        // the top nav, Akita stores, and component state. A SPA transition would leak that into
        // the no-sisu-account page — a security concern. The browser-level navigation tears
        // down the JS context so the next render starts from a clean slate.
        // clear() is kept as a defensive in-memory wipe for the sliver of time between
        // location.assign() returning and the actual unload.
        this.authService.clear();
        const url = this.stateService.href(this.stateName, { reason: accessDeniedType });
        this.navigationService.navigate(url);
    }
}
